import { connectAudioContext } from 'lib/elementAudioContext'
import React, { useCallback, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isHLS } from 'helpers/isHLS'
import { isMediaServerUrl } from 'helpers/isMediaServerUrl'
import { secondsToTimelineTime, timelineTimeToSeconds } from '~/Util'
import * as Actions from '~/actions'
import { useAction, useTap } from '~/hooks'
import { AudioAsset } from '~/models/Asset'
import * as Selectors from '~/selectors'
import BasePreviewPlayer from '../BasePreviewPlayer'
import { usePlayer } from '../lib'
import useAudioContext from '../lib/useAudioContext'
import {
  useAssetProgress,
  usePlayerProgressHandler
} from './lib'
import { SliderTimeContext } from '~/contexts/SliderTimeContext'

const noop = () => {}

function useAssetPlayerHandlers(asset, {
  onReady = noop,
  mode = 'default',
  isBackground,
}) {
  const { shouldRewind, latestTimelineRewindTime } = useAssetProgress(asset)
  const [
    setPlayerInstance,
    playerApi,
  ] = usePlayer({ shouldRewind, latestTimelineRewindTime, isBackground, mode, asset })


  playerApi.usePlayerEffect()

  const onProgress = usePlayerProgressHandler(asset, playerApi.getState)

  return {
    onProgress,
    onReady: useTap(onReady, setPlayerInstance),
  }
}

// ---

function TimelineAssetMediaPlayer(props) {
  const {
    /** @type {Assets.VideoAsset|Assets.AudioAsset} */
    asset,
    /** @type {Assets.VideoAsset|undefined} */
    onReady: baseOnReady,
    playing, muted, volume,
    playAudioOnly,
    style,
    active,
    mode,
    videoRef,
    isMedia,
    addTimelinePlayers,
    height,
    id,
    isBackground,
    isPrimary,
    isComposed,
    mediaTransitions,
    onBufferingProgress,
    onMounted,
    showThumbnail,
    width,
  } = props

  const { onReady, onProgress } = useAssetPlayerHandlers(asset, {
    onReady: baseOnReady,
    mode,
    isBackground,
    isComposed,
  })

  const [ setGainNode,
    setAudioSourceNode ] = useAudioContext({ gain: Math.max(1, asset.volume), asset, videoRef })
  const correctAssetDuration = useAction(Actions.layer.correctAssetDuration, asset.id)

  const layer = useSelector(state => Selectors.getLayerById(state, asset.layerId))
  const currentPreviewAsset = useSelector(Selectors.selectCurrentPreviewAsset)
  const { setLocalTime, localTimeRef } = useContext(SliderTimeContext)

  // NOTE: issue 749: media stops playing in preview on second file
  // It's because duration value recieved from backend greater than real video duration
  const onVideoPlayerReady = useCallback(async player => {
    if (!isHLS(asset.url) && !isMediaServerUrl(asset.url)) {
      const { gainNode,
        source } = connectAudioContext(player.getInternalPlayer())
      setGainNode(gainNode)
      setAudioSourceNode(source)
    }
    const playerDuration = secondsToTimelineTime(player.getDuration())
    if (player.getId() === asset.id && playerDuration < asset.duration) {
      await correctAssetDuration(playerDuration)
    }
    onReady(player)
  }, [ asset, setGainNode, setAudioSourceNode, onReady, correctAssetDuration ])

  const handleChangeDuration = useCallback(duration => {
    const playerDuration = secondsToTimelineTime(duration)
    // issue 3262
    if (currentPreviewAsset?.id === asset.id && playerDuration < asset.duration) {
      correctAssetDuration(playerDuration)
    }
  }, [ asset, correctAssetDuration, currentPreviewAsset ])

  const handleEnded = useCallback(() => {
    const endDuration = localTimeRef.current - asset.startTime
    // issue 3262
    if (endDuration < asset.duration) {
      setLocalTime(asset.endTime, { isRewind: true })
    }
  }, [ asset, setLocalTime, localTimeRef ])

  const isVideoHidden = asset instanceof AudioAsset || playAudioOnly

  const mixedVolume = volume * Math.min(1, asset.volume)

  const styles = useMemo(() => ({ ...style,
    ...(isVideoHidden ? { zIndex: 0 } : { zIndex: 2 }),
    visibility: 'hidden' }), [ isVideoHidden, style ])

  return (
    <If condition={layer}>
      <BasePreviewPlayer
        ref={videoRef}
        onProgress={mode === 'test' ? noop : onProgress}
        playing={playing}
        muted={muted || layer.muted || asset.muted}
        volume={mixedVolume}
        playAudioOnly={playAudioOnly}
        src={asset.url}
        active={active}
        mode={mode}
        isMedia={isMedia}
        addTimelinePlayers={addTimelinePlayers}
        height={height}
        id={id}
        isBackground={isBackground}
        isComposed={isComposed}
        isPrimary={isPrimary}
        mediaTransitions={mediaTransitions}
        onBufferingProgress={onBufferingProgress}
        onMounted={onMounted}
        showThumbnail={showThumbnail}
        width={width}
        startPositionSeconds={timelineTimeToSeconds(asset.mediaStart)}
        onReady={onVideoPlayerReady}
        onDuration={handleChangeDuration}
        onEnded={handleEnded}
        style={styles}
      />
    </If>
  )
}

export default TimelineAssetMediaPlayer
